import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, ChevronRight, Loader2, Pencil, Plus, RefreshCw, Search, Truck, X } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import DeleteWithReasonDialog from "../components/DeleteWithReasonDialog";
import { SUPPLIER_CATEGORIES, canLinkSuppliers, canManageSuppliers, canSeeSupplierSpend, emptySupplierForm, newKey, procurementError, supplierCategoryLabel, supplierPayload, supplierProblem } from "../lib/procurement";
import { formatDateTime } from "../lib/tickets";
import { ExportButton, Field } from "../components/procurement/ProcurementBits";
import { SupplierSpendCard, UnlinkedSuppliers } from "../components/procurement/SupplierLinks";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../components/ui/sheet";

const ALL = "__all__";
const ACTIVE_TABS = [["active", "Actifs"], ["inactive", "Désactivés"], ["all", "Tous"]];
const HISTORY_LABELS = { supplier_created: "Fournisseur ajouté", supplier_updated: "Fiche modifiée", supplier_deactivated: "Désactivé", supplier_reactivated: "Réactivé" };

function SupplierDialog({ supplier, onClose, onDone }) {
  const editing = !!supplier;
  const [form, setForm] = useState(() => emptySupplierForm(supplier));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [key, setKey] = useState(newKey);
  const set = (patch) => { setForm((f) => ({ ...f, ...patch })); setKey(newKey()); };
  const problem = supplierProblem(form);
  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const body = supplierPayload(form);
      const { data } = editing
        ? await api.patch(`/procurement/suppliers/${supplier.id}`, { ...body, expected_version: supplier.version })
        : await api.post("/procurement/suppliers", body, { headers: { "Idempotency-Key": key } });
      toast.success(editing ? "Fournisseur modifié" : "Fournisseur ajouté");
      onDone(data);
    } catch (e) {
      const existing = e?.response?.data?.detail?.existing;
      setError(existing ? `Un fournisseur porte déjà ce nom : ${existing.number} — ${existing.name}.` : procurementError(e, "Enregistrement impossible. Réessayez."));
    } finally {
      setBusy(false);
    }
  };
  const text = (id, label, rest = {}) => (
    <div className="space-y-1.5"><Label htmlFor={`sup-${id}`}>{label}</Label>
      <Input id={`sup-${id}`} value={form[id]} onChange={(e) => set({ [id]: e.target.value })} className="h-10" data-testid={`supplier-${id}`} {...rest} /></div>
  );
  return (
    <Dialog open onOpenChange={(open) => { if (!open && !busy) onClose(); }}>
      <DialogContent data-testid="supplier-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Modifier le fournisseur" : "Nouveau fournisseur"}</DialogTitle>
          <DialogDescription>{editing ? supplier.number : "Son nom est unique : la casse et les accents ne comptent pas."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {text("name", "Nom", { maxLength: 120 })}
          <div className="space-y-1.5">
            <Label>Catégorie</Label>
            <Select value={form.category} onValueChange={(category) => set({ category })}>
              <SelectTrigger className="h-10" aria-label="Catégorie" data-testid="supplier-category"><SelectValue /></SelectTrigger>
              <SelectContent>{SUPPLIER_CATEGORIES.map(([id, label]) => <SelectItem key={id} value={id}>{label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">{text("contact_name", "Personne de contact", { maxLength: 120 })}{text("contact_phone", "Téléphone", { maxLength: 40 })}</div>
          {text("contact_email", "E-mail", { maxLength: 120, type: "email" })}
          <div className="grid gap-3 sm:grid-cols-2">{text("country", "Pays", { maxLength: 120 })}{text("payment_terms", "Conditions de paiement", { maxLength: 300 })}</div>
          {text("address", "Adresse", { maxLength: 300 })}
          <div className="space-y-1.5"><Label htmlFor="sup-notes">Notes</Label>
            <Textarea id="sup-notes" rows={2} maxLength={1000} value={form.notes} onChange={(e) => set({ notes: e.target.value })} data-testid="supplier-notes" /></div>
          {error && <p className="text-sm text-red-600" role="alert" data-testid="supplier-error">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button onClick={submit} disabled={busy || !!problem} title={problem || undefined} data-testid="supplier-submit">{busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{editing ? "Enregistrer" : "Ajouter"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ActiveDialog({ supplier, onClose, onDone }) {
  const deactivating = supplier.active;
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/procurement/suppliers/${supplier.id}/${deactivating ? "deactivate" : "reactivate"}`, { expected_version: supplier.version, reason: reason.trim() || null });
      toast.success(deactivating ? "Fournisseur désactivé" : "Fournisseur réactivé");
      onDone(data);
    } catch (e) {
      setError(procurementError(e, "Impossible de continuer. Réessayez."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open && !busy) onClose(); }}>
      <DialogContent data-testid="supplier-active-dialog">
        <DialogHeader>
          <DialogTitle>{deactivating ? `Désactiver ${supplier.name} ?` : `Réactiver ${supplier.name} ?`}</DialogTitle>
          <DialogDescription>{deactivating ? "Il n'est plus proposé pour de nouveaux achats. Son historique est conservé : un fournisseur n'est jamais supprimé." : "Il redevient proposé pour de nouveaux achats."}</DialogDescription>
        </DialogHeader>
        {deactivating && (
          <div className="space-y-1.5"><Label htmlFor="sup-reason">Motif</Label>
            <Textarea id="sup-reason" rows={2} maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="supplier-reason" /></div>
        )}
        {error && <p className="text-sm text-red-600" role="alert" data-testid="supplier-active-error">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button variant={deactivating ? "destructive" : "default"} onClick={submit} disabled={busy || (deactivating && reason.trim().length < 3)} data-testid="supplier-active-submit">
            {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{deactivating ? "Désactiver" : "Réactiver"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SupplierSheet({ id, canManage, onClose, onChanged }) {
  const { user } = useAuth();
  const [supplier, setSupplier] = useState(null);
  const [phase, setPhase] = useState("loading");
  const [dialog, setDialog] = useState(null);
  const load = useCallback(async () => {
    setPhase("loading");
    try {
      const { data } = await api.get(`/procurement/suppliers/${id}`);
      setSupplier(data);
      setPhase("ready");
    } catch {
      setPhase("error");
    }
  }, [id]);
  useEffect(() => { if (id) load(); }, [id, load]);
  const changed = () => { setDialog(null); load(); onChanged(); };
  return (
    <Sheet open={!!id} onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="supplier-sheet">
        <SheetHeader>
          <SheetTitle className="pr-6 text-left">{supplier?.name || "Fournisseur"}</SheetTitle>
          <SheetDescription className="text-left">{supplier ? `${supplier.number} · ${supplierCategoryLabel(supplier.category)}` : ""}</SheetDescription>
        </SheetHeader>
        {phase === "loading" && <div className="space-y-2 py-4"><Skeleton className="h-6 w-full" /><Skeleton className="h-24 w-full" /></div>}
        {phase === "error" && <p className="py-4 text-sm text-slate-600" data-testid="supplier-sheet-error">Impossible de charger ce fournisseur.<Button variant="link" onClick={load}>Réessayer</Button></p>}
        {phase === "ready" && supplier && (
          <div className="space-y-5 py-4">
            <div className="flex flex-wrap items-center gap-2">
              {!supplier.active && <Badge variant="outline" data-testid="supplier-inactive-badge">Désactivé</Badge>}
              {canManage && <Button variant="outline" size="sm" onClick={() => setDialog("edit")} data-testid="supplier-edit"><Pencil size={14} className="mr-1.5" />Modifier</Button>}
              {canManage && <Button variant="outline" size="sm" onClick={() => setDialog("active")} data-testid="supplier-toggle">{supplier.active ? "Désactiver" : "Réactiver"}</Button>}
              {canManage && hasPermission(user, "procurement:delete") && <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => setDialog("delete")} data-testid="supplier-delete">Supprimer…</Button>}
            </div>
            <dl className="divide-y divide-slate-100">
              {supplier.contact_name && <Field label="Contact">{supplier.contact_name}</Field>}
              {supplier.contact_email && <Field label="E-mail"><a className="text-jamora hover:underline" href={`mailto:${supplier.contact_email}`}>{supplier.contact_email}</a></Field>}
              {supplier.contact_phone && <Field label="Téléphone">{supplier.contact_phone}</Field>}
              {supplier.country && <Field label="Pays">{supplier.country}</Field>}
              {supplier.address && <Field label="Adresse">{supplier.address}</Field>}
              {supplier.payment_terms && <Field label="Paiement">{supplier.payment_terms}</Field>}
              {supplier.notes && <Field label="Notes"><span className="whitespace-pre-wrap">{supplier.notes}</span></Field>}
            </dl>
            {canSeeSupplierSpend(user) && <SupplierSpendCard supplierId={supplier.id} />}
            <div>
              <h3 className="text-sm font-semibold text-slate-900 mb-2">Historique</h3>
              <ol className="space-y-2" data-testid="supplier-history">
                {supplier.history.map((h) => (
                  <li key={h.id} className="border-l-2 border-slate-200 pl-3">
                    <div className="text-sm text-slate-900">{HISTORY_LABELS[h.kind] || h.kind}</div>
                    <div className="text-xs text-slate-500">{formatDateTime(h.at)}{h.actor_name ? ` · ${h.actor_name}` : ""}</div>
                    {h.reason && <div className="text-xs text-slate-600">Motif : {h.reason}</div>}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}
        <DeleteWithReasonDialog
          target={dialog === "delete" ? supplier : null} testId="delete-supplier" onClose={() => setDialog(null)}
          title={`Supprimer le fournisseur ${supplier?.number} ?`}
          description="Il va dans la corbeille et se restaure pendant 30 jours. Un fournisseur déjà utilisé (devis, actif, dépense) se désactive à la place."
          onConfirm={async (reason) => {
            try {
              await api.delete(`/procurement/suppliers/${supplier.id}`, { data: { reason } });
              toast.success("Mis à la corbeille");
              onClose();
              onChanged();
            } catch (e) {
              toast.error(procurementError(e, "Impossible de supprimer cet élément."));
              setDialog(null);
            }
          }}
        />
        {dialog === "edit" && supplier && <SupplierDialog supplier={supplier} onClose={() => setDialog(null)} onDone={changed} />}
        {dialog === "active" && supplier && <ActiveDialog supplier={supplier} onClose={() => setDialog(null)} onDone={changed} />}
      </SheetContent>
    </Sheet>
  );
}

export default function ProcurementSuppliers() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const active = ACTIVE_TABS.some(([id]) => id === params.get("active")) ? params.get("active") : "active";
  const category = params.get("category") || "";
  const search = params.get("q") || "";
  const [searchInput, setSearchInput] = useState(search);
  const [data, setData] = useState({ items: [], total: 0 });
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState("");
  const [open, setOpen] = useState(null);
  const [creating, setCreating] = useState(false);
  const seq = useRef(0);
  const canManage = canManageSuppliers(user);

  const update = (changes) => setParams((prev) => {
    const next = new URLSearchParams(prev);
    for (const [k, v] of Object.entries(changes)) { if (v) next.set(k, v); else next.delete(k); }
    return next;
  }, { replace: true });
  useEffect(() => {
    const t = setTimeout(() => { if (searchInput !== search) update({ q: searchInput.trim() }); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  const query = useMemo(() => ({ limit: 200, active, ...(category ? { category } : {}), ...(search ? { q: search } : {}) }), [active, category, search]);
  const load = useCallback(async () => {
    const mine = ++seq.current;
    setPhase((p) => (p === "ready" ? p : "loading"));
    try {
      const { data: body } = await api.get("/procurement/suppliers", { params: query });
      if (mine !== seq.current) return;
      setData(body);
      setPhase("ready");
    } catch (e) {
      if (mine !== seq.current) return;
      setError(procurementError(e, "Impossible de charger les fournisseurs."));
      setPhase("error");
    }
  }, [query]);
  useEffect(() => { load(); return () => { seq.current += 1; }; }, [load]);

  const filtered = !!(category || search);
  const reset = () => { setSearchInput(""); setParams({}, { replace: true }); };

  return (
    <div className="space-y-4" data-testid="procurement-suppliers-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display font-bold text-2xl text-slate-900">Fournisseurs</h1>
          <p className="text-sm text-slate-500">Ceux à qui Jamora achète : coordonnées, conditions de paiement et historique.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ExportButton kind="suppliers" />
          {canManage && <Button onClick={() => setCreating(true)} data-testid="supplier-new"><Plus size={16} className="mr-1.5" />Nouveau fournisseur</Button>}
        </div>
      </div>
      {canLinkSuppliers(user) && <UnlinkedSuppliers onLinked={load} />}

      <div className="flex gap-1 flex-wrap" role="tablist" aria-label="Fournisseurs">
        {ACTIVE_TABS.map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={active === id} onClick={() => update({ active: id === "active" ? "" : id })} data-testid={`suppliers-tab-${id}`}
            className={`rounded-full px-3 py-1.5 text-sm font-medium whitespace-nowrap ${active === id ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>{label}</button>
        ))}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative basis-full sm:basis-auto sm:flex-1 min-w-0 md:max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Nom du fournisseur…" className="pl-9 h-9" aria-label="Rechercher un fournisseur" data-testid="suppliers-search" />
        </div>
        <Select value={category || ALL} onValueChange={(v) => update({ category: v === ALL ? "" : v })}>
          <SelectTrigger className="h-9 w-[calc(50%-4px)] sm:w-48" aria-label="Catégorie" data-testid="suppliers-category"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>Toutes les catégories</SelectItem>{SUPPLIER_CATEGORIES.map(([id, label]) => <SelectItem key={id} value={id}>{label}</SelectItem>)}</SelectContent>
        </Select>
        {filtered && <Button variant="ghost" size="sm" className="h-9 text-slate-600" onClick={reset}><X size={14} className="mr-1" />Effacer</Button>}
      </div>

      {phase === "loading" && <div className="space-y-2"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></div>}
      {phase === "error" && (
        <Card className="p-6 text-center space-y-3">
          <AlertTriangle className="mx-auto text-amber-500" size={20} />
          <p className="text-sm text-slate-700">{error}</p>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </Card>
      )}
      {phase === "ready" && data.items.length === 0 && (
        <Card className="p-8 text-center space-y-2" data-testid="suppliers-empty">
          <Truck className="mx-auto text-slate-300" size={26} />
          {filtered ? <><h3 className="font-medium text-slate-900">Aucun fournisseur ne correspond</h3><Button variant="outline" size="sm" onClick={reset}>Effacer les filtres</Button></> : (
            <><h3 className="font-medium text-slate-900">{active === "inactive" ? "Aucun fournisseur désactivé" : "Aucun fournisseur pour le moment"}</h3>
              <p className="text-sm text-slate-500 max-w-md mx-auto">{canManage ? "Ajoutez ceux à qui vous achetez : vous retrouverez leurs coordonnées et leurs conditions au même endroit." : "Les fournisseurs apparaîtront ici."}</p>
              {canManage && active !== "inactive" && <Button size="sm" onClick={() => setCreating(true)}><Plus size={14} className="mr-1.5" />Nouveau fournisseur</Button>}</>
          )}
        </Card>
      )}
      {phase === "ready" && data.items.length > 0 && (
        <>
          <Card className="hidden md:block overflow-hidden" data-testid="suppliers-table">
            <Table>
              <TableHeader><TableRow><TableHead>Fournisseur</TableHead><TableHead>Catégorie</TableHead><TableHead>Contact</TableHead><TableHead className="hidden lg:table-cell">Paiement</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.items.map((s) => (
                  <TableRow key={s.id} className="cursor-pointer" onClick={() => setOpen(s.id)} data-testid={`supplier-row-${s.number}`}>
                    <TableCell><div className="font-medium text-slate-900">{s.name}{!s.active && <Badge variant="outline" className="ml-2">Désactivé</Badge>}</div><div className="text-xs text-slate-500 tabular-nums">{s.number}</div></TableCell>
                    <TableCell className="text-sm">{supplierCategoryLabel(s.category)}</TableCell>
                    <TableCell className="text-sm">{s.contact_name || s.contact_email || s.contact_phone || <span className="text-slate-400">—</span>}</TableCell>
                    <TableCell className="hidden lg:table-cell text-sm">{s.payment_terms || <span className="text-slate-400">—</span>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <Card className="md:hidden divide-y divide-slate-100" data-testid="suppliers-cards">
            {data.items.map((s) => (
              <button key={s.id} type="button" onClick={() => setOpen(s.id)} className="flex w-full items-start gap-3 px-4 py-3 text-left" data-testid={`supplier-card-${s.number}`}>
                <div className="min-w-0 flex-1 space-y-0.5">
                  <div className="font-medium text-slate-900">{s.name}{!s.active && <Badge variant="outline" className="ml-2">Désactivé</Badge>}</div>
                  <div className="text-xs text-slate-500">{s.number} · {supplierCategoryLabel(s.category)}</div>
                  {(s.contact_name || s.contact_phone) && <div className="text-sm text-slate-700">{[s.contact_name, s.contact_phone].filter(Boolean).join(" · ")}</div>}
                </div>
                <ChevronRight size={16} className="text-slate-300 shrink-0 mt-1" />
              </button>
            ))}
          </Card>
          <p className="text-xs text-slate-500" data-testid="suppliers-count">{data.total} fournisseur{data.total > 1 ? "s" : ""}</p>
        </>
      )}

      {creating && <SupplierDialog onClose={() => setCreating(false)} onDone={(s) => { setCreating(false); load(); setOpen(s.id); }} />}
      <SupplierSheet id={open} canManage={canManage} onClose={() => setOpen(null)} onChanged={load} />
    </div>
  );
}
